import { useCallback, useEffect, useRef, useState } from 'react'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import { PriceFormField, Range } from '../../../../components/inputs'
import { Button } from '../../../../components/button'
import { Mobile } from '../../../../components/media'

export default function PriceFilter({
  price,
  errors,
  numberOfCarsInRange = '0',
  onChange,
  handleFilterToggle,
}) {
  const [state, setState] = useState({ start: price.start, end: price.end })

  const handleDebouncedChange = useDebouncedCallback(onChange, 500, [onChange])

  const hydratedRef = useRef()
  useEffect(() => {
    if (hydratedRef.current) {
      handleDebouncedChange(state)
    }

    hydratedRef.current = true
  }, [handleDebouncedChange, state])

  const handlePriceChange = useCallback((event) => {
    // TODO: Remove event.persist() after React 17 release
    event.persist()
    let cleanedNumber = event.target.value.replace(/[^0-9]/g, '')
    setState((prev) => ({ ...prev, [event.target.name]: cleanedNumber }))
  }, [])

  const handlePriceBlur = useCallback(() => {
    setState((prev) => (prev.start > prev.end ? { start: prev.start, end: prev.start } : prev))
  }, [])

  const handleRangeChange = useCallback(([start, end]) => {
    setState({ start: start.toString(), end: end.toString() })
  }, [])

  return (
    <div className='wrapper'>
      <p className='filterLabel'>Price Range</p>

      <div className='inputsContainer flex alignCenter'>
        <PriceFormField
          className='price rangeStart'
          name='start'
          value={state.start}
          onChange={handlePriceChange}
          onBlur={handlePriceBlur}
          error={errors.priceStart}
        />
        <span>to</span>
        <PriceFormField
          className='price rangeEnd'
          name='end'
          value={state.end}
          onChange={handlePriceChange}
          onBlur={handlePriceBlur}
          error={errors.priceEnd}
        />
      </div>

      <div className='rangeContainer'>
        <Range
          pearling
          step={100}
          min={100}
          max={250_000}
          onChange={handleRangeChange}
          value={[Math.min(state.start, state.end), Math.max(state.start, state.end)]}
        />
        <div className='rangeValues flex justifyBetween'>
          <span>$ {Number(state.start).toLocaleString()}</span>
          <span>
            $ {Number(state.end) === 250_000 ? 'Unlimited' : Number(state.end).toLocaleString()}
          </span>
        </div>
      </div>

      <div className='numberOfCarsInRange'>
        <b>{numberOfCarsInRange.toLocaleString()}</b>
        <span>cars within budget</span>
      </div>
      <Mobile>
        <div className='applyButton'>
          <Button onClick={() => handleFilterToggle('price')}>Apply</Button>
        </div>
      </Mobile>

      <style jsx>{`
        .applyButton {
          position: absolute;
          left: calc(50% - 46.9px);
          margin-top: 5rem;
        }
        .wrapper {
          height: 23.2rem;
          padding: 2rem;
          border-radius: 1rem;
          background: #fff;
          box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
          color: var(--secondary-text-color);
        }

        .filterLabel {
          font-size: 1.6rem;
          font-weight: 700;
        }

        .inputsContainer {
          margin: 1.1rem 0 3.2rem;

          > span {
            margin: 0 1.6rem;
            font-size: 1.6rem;
          }

          :global(.inputContainer) {
            width: 100%;
            color: var(--primary-text-color);
          }
        }

        .rangeValues {
          margin: 0.5rem 0 0;
          font-size: 1.2rem;
        }

        .numberOfCarsInRange {
          margin: 1.8rem 0 0;
          padding: 1.5rem 0 0;
          border-top: 0.1rem solid var(--separator-line-color);
          text-align: center;
          font-size: 1.6rem;

          b {
            margin: 0 0.5rem 0 0;
            color: var(--primary-text-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            max-width: 100%;
            padding: 3rem;
            box-shadow: none;
            border-radius: 0;
          }

          .filterLabel {
            font-size: 1.2rem;
          }

          .numberOfCarsInRange {
            font-size: 1.4rem;
          }
        }
      `}</style>
    </div>
  )
}
